"use client";

import { Box, Container } from "@mui/material";
import { usePathname, useRouter } from "next/navigation";
import React, { useCallback, useEffect, useState } from "react";
import { HeaderNavInterface, SocialGroupInterface } from "src/lib/utils/interfaces";
import { SalifyHeaderLogoWrapper } from "./common/logo-wrapper";
import {
  NovaPressDesktopHotlineBlock,
  NovaPressDesktopNavLinks,
  NovaPressHeaderTopBar,
  NovaPressResponsiveHeaderNav,
} from "./nove-press/components";

interface VariantProps {
  logoUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
  name: string | undefined;
  hotline: string | undefined;
  topBarInfoText: string | undefined;
  nav: Array<HeaderNavInterface> | undefined;
  socialGroup: Array<SocialGroupInterface> | undefined;
  socialSpacing?: number;
  topBarBgColor?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
}

const HeaderNovaPress: React.FC<VariantProps> = (props) => {
  const [activeSection, setActiveSection] = useState<string | undefined>();
  const pathname = usePathname();
  const router = useRouter();

  // Khi từ 1 trang khác navigate đến 1 section trong trang home xử lý để scroll tới section đó
  // và xóa param "scrollTo" khỏi URL
  useEffect(() => {
    const timeout = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const targetId = params.get("scrollTo");

      if (targetId) {
        const el = document.getElementById(targetId);
        if (el) {
          // Scroll và xóa param khỏi URL
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.delete("scrollTo");
      window.history.replaceState({}, "", url.toString());
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  // Lắng nghe để active nav item được scroll tới
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleSections = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => entry.target.getAttribute("id"))
          .filter(Boolean) as string[];

        if (visibleSections.length > 0) {
          setActiveSection(visibleSections[0]);
        }
      },
      {
        rootMargin: "0px 0px -50% 0px",
        threshold: 0.2,
      },
    );

    const sectionElements = props.nav
      ?.filter((item) => item.action === "scroll" && item.target)
      .map((item) => document.getElementById(item.target!.replace("#", "")))
      .filter((el): el is HTMLElement => !!el);

    sectionElements?.forEach((el) => observer.observe(el));

    return () => {
      sectionElements?.forEach((el) => observer.unobserve(el));
    };
  }, [props.nav]);

  // Khi back lại active đúng section nav trước đó
  useEffect(() => {
    const timeout = setTimeout(() => {
      const sectionElements = props.nav
        ?.filter((item) => item.action === "scroll" && item.target)
        .map((item) => {
          const id = item.target!.replace("#", "");
          return document.getElementById(id);
        })
        .filter((el): el is HTMLElement => !!el);

      if (sectionElements && sectionElements.length > 0) {
        for (const el of sectionElements) {
          const rect = el.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
            setActiveSection(el.id);
            break;
          }
        }
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [props.nav]);

  const isNavItemActive = useCallback(
    (item: HeaderNavInterface, sectionId?: string): boolean => {
      if (item.action === "scroll" || item.action === "popover") {
        return activeSection === sectionId || (activeSection === undefined && item.target === "/");
      }
      return pathname !== "/" && !!item.target && pathname.startsWith(`/${item.target}`);
    },
    [activeSection, pathname],
  );

  const onClickNavItem = (item: HeaderNavInterface, sectionId: string | undefined) => {
    if (item.action !== "scroll") {
      return;
    }

    // Scroll to top if home
    if (item.target === "/" && pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (pathname !== "/") {
      router.push(`/?scrollTo=${sectionId}`);
    } else {
      const el = document.getElementById(sectionId ?? "");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  return (
    <>
      <NovaPressHeaderTopBar
        name={props.name}
        topBarInfoText={props.topBarInfoText}
        socialGroup={props.socialGroup}
        socialSpacing={props.socialSpacing}
        topBarBgColor={props.topBarBgColor}
        showSearch={props.showSearch}
        searchPlaceholder={props.searchPlaceholder}
      />

      <Box
        sx={(theme) => ({
          bgcolor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: theme.zIndex.appBar,
          overflow: "visible",
        })}
      >
        <Box sx={{ position: "relative" }}>
          <Container
            disableGutters
            sx={{
              display: { xs: "flex", laptop: "grid" },
              flexDirection: "row",
              justifyContent: { xs: "flex-start", laptop: "stretch" },
              alignItems: { xs: "center", laptop: "stretch" },
              gridTemplateColumns: { laptop: "auto minmax(0, 1fr) auto" },
              columnGap: { laptop: 1 },
              minHeight: { xs: 60, laptop: 72 },
              py: { xs: 0.5, laptop: 0.75 },
              paddingX: 0,
              overflow: "visible",
              pr: props?.nav?.length ? { xs: 12, laptop: 0 } : 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, minWidth: 0 }}>
              <SalifyHeaderLogoWrapper
                logoUrl={props.logoUrl}
                logoWidth={props.logoWidth}
                logoHeight={props.logoHeight}
              />
            </Box>

            {!!props?.nav?.length && (
              <Box
                sx={{
                  display: { xs: "none", laptop: "flex" },
                  justifyContent: "center",
                  alignItems: "stretch",
                  minWidth: 0,
                }}
              >
                <NovaPressDesktopNavLinks
                  nav={props.nav}
                  isActive={isNavItemActive}
                  onClick={onClickNavItem}
                  showSearch={props.showSearch}
                  searchPlaceholder={props.searchPlaceholder}
                />
              </Box>
            )}

            {!!props?.nav?.length && (
              <Box
                sx={{
                  display: { xs: "none", laptop: "flex" },
                  alignItems: "center",
                  justifyContent: "flex-end",
                  borderLeft: props.hotline ? "1px solid" : "none",
                  borderColor: "grey.400",
                  pl: props.hotline ? 2 : 0,
                }}
              >
                <NovaPressDesktopHotlineBlock hotline={props.hotline} />
              </Box>
            )}
          </Container>

          {!!props?.nav?.length && (
            <Box
              component="nav"
              aria-label="Menu điều hướng"
              sx={{
                display: { xs: "flex", laptop: "none" },
                position: "absolute",
                right: 4,
                top: 0,
                bottom: 0,
                alignItems: "center",
                zIndex: 50,
                pointerEvents: "auto",
                touchAction: "manipulation",
              }}
            >
              <NovaPressResponsiveHeaderNav
                nav={props.nav}
                socialGroup={props.socialGroup}
                isActive={isNavItemActive}
                onClick={onClickNavItem}
                hotline={props.hotline}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

HeaderNovaPress.displayName = "HeaderNovaPress";

export default HeaderNovaPress;
